import React, { useState } from "react";
import {
  IconButton,
  InputBase,
  Button,
  Paper,
  Box,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import BookmarksIcon from "@material-ui/icons/BookmarksOutlined";

interface FindReplaceWidgetProps {
  onClose: () => void;
  findText: string;
  setFindText: (val: string) => void;
  replaceText: string;
  setReplaceText: (val: string) => void;
  onExecuteReplace: (replaceSingle: boolean) => void;
  matchCount: number;
  currentIndex: number;
  onNext: () => void;
  onPrev: () => void;
}

export function FindReplaceWidget({
  onClose,
  findText,
  setFindText,
  replaceText,
  setReplaceText,
  onExecuteReplace,
  matchCount,
  currentIndex,
  onNext,
  onPrev,
}: FindReplaceWidgetProps) {
  const [activeTab, setActiveTab] = useState<"find" | "replace">("find");

  const searchInput = (
    <Box
        style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#ffffff",
            borderRadius: 6,
            border: "1px solid #9ca3af",
            padding: "4px 10px",
            marginBottom: 12,
        }}
    >
        <SearchIcon
            style={{
                color: "#555",
                fontSize: 20,
                marginRight: 8,
            }}
        />

        <InputBase
            placeholder="Search..."
            value={findText}
            onChange={(e) => {
                setFindText(e.target.value);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
                flex: 1,
                fontSize: 14,
                color: "#000",
                minWidth: 0,
            }}
        />

        {findText.trim() && (
            <Typography
                variant="caption"
                style={{
                    color: "#555",
                    whiteSpace: "nowrap",
                    marginLeft: 8,
                    fontSize: 12,
                    fontWeight: 500,
                }}
            >
                {matchCount > 0
                    ? `${currentIndex + 1} / ${matchCount}`
                    : "0 / 0"}
            </Typography>
        )}
    </Box>
);

  return (
    <Paper
      elevation={6}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: 60,
        right: 20,
        width: 320,
        backgroundColor: "#e8eaed",
        border: "1px solid #9ca3af",
        borderRadius: 8,
        padding: 16,
        zIndex: 99999,
        boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.25)",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 600, color: "#111", fontSize: "15px" }}
        >
          Find and Replace ▾
        </Typography>
        <Box>
          <IconButton
            size="small"
            onClick={onClose}
            style={{ color: "#333", padding: 6 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        style={{
          display: "flex",
          backgroundColor: "#d1d5db",
          borderRadius: 6,
          padding: 3,
          marginBottom: 16,
        }}
      >
        <Button
          style={{
            flex: 1,
            textTransform: "none",
            borderRadius: 4,
            minHeight: 30,
            padding: "4px 0",
            backgroundColor: activeTab === "find" ? "#ffffff" : "transparent",
            boxShadow:
              activeTab === "find" ? "0 1px 4px rgba(0,0,0,0.2)" : "none",
            fontWeight: activeTab === "find" ? 600 : 500,
          }}
          onClick={() => setActiveTab("find")}
        >
          Find
        </Button>
        <Button
          style={{
            flex: 1,
            textTransform: "none",
            borderRadius: 4,
            minHeight: 30,
            padding: "4px 0",
            backgroundColor:
              activeTab === "replace" ? "#ffffff" : "transparent",
            boxShadow:
              activeTab === "replace" ? "0 1px 4px rgba(0,0,0,0.2)" : "none",
            fontWeight: activeTab === "replace" ? 600 : 500,
          }}
          onClick={() => setActiveTab("replace")}
        >
          Replace
        </Button>
      </Box>

      {activeTab === "find" && (
        <>
          {searchInput}
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!findText}
              onClick={onPrev}
              style={{ flex: 1, textTransform: "none" }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!findText}
              onClick={onNext}
              style={{ flex: 1, textTransform: "none" }}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {activeTab === "replace" && (
        <>
          {searchInput}
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#ffffff",
              borderRadius: 6,
              border: "1px solid #9ca3af",
              padding: "4px 10px",
              marginBottom: 16,
            }}
          >
            <InputBase
    placeholder="Enter replacement"
    value={replaceText}
    onChange={(e) => setReplaceText(e.target.value)}
    onMouseDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
    style={{
        flex: 1,
        fontSize: 14,
        color: "#000",
    }}
/>
          </Box>
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onExecuteReplace(true)}
              disabled={matchCount === 0}
              style={{
                flex: 1,
                textTransform: "none",
                backgroundColor: "#ffffff",
                fontWeight: 600,
              }}
            >
              Replace
            </Button>
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() => onExecuteReplace(false)}
              disabled={matchCount === 0}
              style={{
                flex: 1,
                textTransform: "none",
                backgroundColor: "#1976d2",
                color: "#ffffff",
                fontWeight: 600,
              }}
            >
              Replace All
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
